import {useContext, useEffect, useState} from "react";
import {useMessages} from "i18n";
import {ToastContext} from "Toast";
import {useUser} from "UserProvider";
import {responseStatus, serverMessage} from "utils/ErrorUtils";
import {useKeyedSingleFlight} from "utils/useSingleFlight";
import {canUseCustomDomain, planOf} from "home/gating";
import {AccountDomainApi} from "../AccountDomainApi";
import {accountApiFailureText} from "../accountApiFailure";
import {
	customDomainSectionState,
	presentCustomDomain,
	validateDomain,
	type CustomDomain,
	type CustomDomainPresentation,
	type CustomDomainSectionState,
} from "../customDomain/customDomain";

/**
 * Contract of {@link useAccountCustomDomain} — the thin React adapter over `CustomDomainService`
 * (kitecorp/transferify-api-service#348, kitecorp/devops#126 phase B). Its domain rules live in plain
 * account modules; this hook only sequences requests and surfaces toasts (epic #26).
 */
export interface UseAccountCustomDomain {
	/** Which of the section's five mutually exclusive states to render. */
	state: CustomDomainSectionState;
	/** The registered domain, or null when there is none. */
	domain: CustomDomain | null;
	/** What to render for that domain, or null when there is none. */
	presentation: CustomDomainPresentation | null;
	/** The host currently in the input. */
	input: string;
	/** True while a request is in flight; the section disables its controls. */
	busy: boolean;
	/** The error to show inside the section, or null. */
	error: string | null;
	/** Whether the confirm dialog for removing is open. */
	confirmOpen: boolean;
	setInput: (value: string) => void;
	register: () => Promise<void>;
	/** Proves ownership and provisions the edge — the single retry affordance. */
	checkDns: () => Promise<void>;
	rotateToken: () => Promise<void>;
	openConfirm: () => void;
	closeConfirm: () => void;
	remove: () => Promise<void>;
}

export function useAccountCustomDomain(): UseAccountCustomDomain {
	const m = useMessages();
	const [user] = useUser();
	const {showErrorToast, showSuccessToast} = useContext(ToastContext);
	const [domain, setDomain] = useState<CustomDomain | null>(null);
	const [input, setInput] = useState("");
	const [busy, setBusy] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const entitled = canUseCustomDomain(planOf(user));

	// Load once, on EVERY tier. The api leaves the read and the removal ungated precisely so a downgraded
	// owner can still see and hand back the host they registered, and skipping the fetch for them is the
	// defect ui#534 reported against the sibling claim editor — the host stays registered against every
	// other account with nobody able to free it. It costs one GET, and only when the sheet is opened:
	// BrandSheet is a MUI modal without `keepMounted`, so this hook does not exist until then.
	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const loaded = await AccountDomainApi.current();
				if (active) {
					setDomain(loaded);
				}
			} catch (e) {
				if (active) {
					showErrorToast(failureText(e));
				}
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		})();
		return () => {
			active = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Keys are per ACTION so a slow verification never blocks the removal that undoes it.
	const flight = useKeyedSingleFlight();

	/**
	 * The api answers in the caller's language (`Accept-Language` rides every request), so its own sentence
	 * is what "reserved", "taken", "not on your plan" and "the record does not match" must read as — the
	 * catalog only supplies the two cases where there is no server sentence at all.
	 */
	const failureText = (e: unknown): string => accountApiFailureText(
		{
			status: responseStatus(e),
			message: serverMessage(e),
		},
		{network: m.account.customDomain.errors.network, generic: m.account.customDomain.errors.generic},
	);

	/** Every mutation is the same shape: one in flight, replace the domain, report failure in place. */
	const mutate = async (key: string, call: () => Promise<CustomDomain>, success: string) => {
		await flight.run(key, async () => {
			setBusy(true);
			setError(null);
			try {
				setDomain(await call());
				showSuccessToast(success);
			} catch (e) {
				setError(failureText(e));
			} finally {
				setBusy(false);
			}
		});
	};

	const register = async () => {
		const verdict = validateDomain(input);
		if (!verdict.valid) {
			setError(m.account.customDomain.errors[verdict.error]);
			return;
		}
		await mutate(
			"register",
			async () => {
				const registered = await AccountDomainApi.register(verdict.domain);
				setInput("");
				return registered;
			},
			m.account.customDomain.toasts.added,
		);
	};

	const checkDns = () => mutate(
		"verify", () => AccountDomainApi.verify(), m.account.customDomain.toasts.verified);

	const rotateToken = () => mutate(
		"rotate", () => AccountDomainApi.rotateVerificationToken(), m.account.customDomain.toasts.tokenRotated);

	const remove = async () => {
		setConfirmOpen(false);
		await flight.run("remove", async () => {
			setBusy(true);
			try {
				await AccountDomainApi.remove();
				setDomain(null);
				setError(null);
				showSuccessToast(m.account.customDomain.toasts.removed);
			} catch (e) {
				// A refused removal keeps the row on the server — the api aborts rather than free a host
				// CloudFront is still serving — so the section must keep showing the domain. Reported as a
				// toast rather than swallowed: this is the one leg allowed to fail and stay failed.
				showErrorToast(failureText(e));
			} finally {
				setBusy(false);
			}
		});
	};

	return {
		state: customDomainSectionState({entitled, loading, hasDomain: domain !== null}),
		domain,
		presentation: domain === null ? null : presentCustomDomain(domain, {entitled}),
		input,
		busy,
		error,
		confirmOpen,
		setInput,
		register,
		checkDns,
		rotateToken,
		openConfirm: () => setConfirmOpen(true),
		closeConfirm: () => setConfirmOpen(false),
		remove,
	};
}
